/**
 * Seeded Random Number Generator
 * 
 * Provides deterministic random numbers for reproducible procedural generation.
 * Uses mulberry32 algorithm for fast, quality pseudo-random numbers.
 */

export class SeededRandom {
  private seed: number;
  private state: number;
  
  /**
   * Create a new seeded random generator
   * @param seed - Initial seed value
   */
  constructor(seed: number = Date.now()) {
    this.seed = seed;
    this.state = seed;
  }
  
  /**
   * Generate next random number between 0 and 1
   * Uses mulberry32 algorithm
   */
  next(): number {
    let t = this.state += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
  
  /**
   * Generate random integer between min and max (inclusive)
   */
  nextInt(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }
  
  /**
   * Generate random float between min and max
   */
  nextFloat(min: number, max: number): number {
    return this.next() * (max - min) + min;
  }
  
  /**
   * Generate random boolean with given probability
   * @param probability - Probability of true (0-1)
   */
  nextBool(probability: number = 0.5): boolean {
    return this.next() < probability;
  }
  
  /**
   * Pick random element from array
   */
  pick<T>(array: readonly T[]): T {
    return array[Math.floor(this.next() * array.length)];
  }
  
  /**
   * Shuffle array using Fisher-Yates algorithm
   */
  shuffle<T>(array: T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }
  
  /**
   * Reset to initial seed
   */
  reset(): void {
    this.state = this.seed;
  }
  
  /**
   * Set new seed
   */
  setSeed(seed: number): void {
    this.seed = seed;
    this.state = seed;
  }
  
  /**
   * Get current seed
   */
  getSeed(): number {
    return this.seed;
  }
}

export default SeededRandom;
